import React from "react";

const T = {
  dark: "#2E4053",
  darkDeep: "#1C2A3D",
  muted: "#34495E",
  border: "#D1D8E0",
  surface: "#F4F6F9",
  white: "#FFFFFF",
  chipText: "#D1D8E0",
};

// ── Static content per sub-type ───────────────────────────────────────────────
const ABOUT_CONTENT = {
  hero: {
    label: "WHO WE ARE",
    title: "Built to Empower Businesses.",
    sub: "Spade ERP was founded with a single belief: every business — regardless of size — deserves access to powerful, affordable, and simple management tools.",
  },
  sections: [
    {
      title: "Our Story",
      body: `Spade ERP was born out of frustration with the existing ERP landscape. SAP, Oracle, and Microsoft Dynamics are powerful — but they're also expensive, complex, and take months to implement. Small and medium businesses in Egypt and across the Middle East were being left behind.\n\nWe set out to build something different: a complete, enterprise-grade ERP platform that any business could deploy in days, not months, at a fraction of the cost.`,
    },
    {
      title: "Our Mission",
      body: "To empower businesses of all sizes with enterprise-grade technology that is simple, affordable, and accessible. We believe every business deserves powerful tools to grow, automate operations, and make better decisions — without spending millions on software.",
    },
    {
      title: "Our Vision",
      body: "To become the leading ERP platform for small and medium businesses across the Middle East, Africa, Europe, and beyond. We envision a future where businesses can manage every aspect of their operations through one intelligent, connected platform.",
    },
  ],
  values: [
    {
      icon: "🎯",
      title: "Customer First",
      desc: "Every decision we make starts with how it impacts our customers' businesses.",
    },
    {
      icon: "💡",
      title: "Simplicity",
      desc: "Power without complexity. If it's not simple, we rebuild it until it is.",
    },
    {
      icon: "🌍",
      title: "Accessibility",
      desc: "Enterprise tools should be available to every business, not just large corporations.",
    },
    {
      icon: "🔒",
      title: "Trust",
      desc: "We handle your business data with the highest standards of security and privacy.",
    },
    {
      icon: "⚡",
      title: "Speed",
      desc: "Fast implementation, fast support, fast updates. We value your time.",
    },
    {
      icon: "🤝",
      title: "Partnership",
      desc: "We don't just sell software — we become a long-term partner in your growth.",
    },
  ],
  stats: [
    { val: "300+", label: "Companies" },
    { val: "5,000+", label: "Active Users" },
    { val: "40+", label: "ERP Modules" },
    { val: "99.9%", label: "Uptime" },
  ],
};

const PRIVACY_CONTENT = {
  hero: {
    label: "LEGAL",
    title: "Privacy Policy",
    sub: `Last updated: January 1, 2026`,
  },
  sections: [
    {
      title: "1. Information We Collect",
      body: "We collect information you provide directly, such as your name, email address, phone number, and company details when you register for Spade ERP or contact us. We also collect usage data, log data, and device information automatically when you use our platform.",
    },
    {
      title: "2. How We Use Your Information",
      body: "We use collected information to provide, maintain, and improve our services; process transactions; send you technical notices and support messages; respond to your comments and questions; and send you information about products and services.",
    },
    {
      title: "3. Data Storage & Security",
      body: "Your data is stored on secure servers located in Egypt and compliant with Egyptian data protection regulations. We implement industry-standard security measures including encryption at rest and in transit, role-based access controls, and regular security audits.",
    },
    // {
    //   title: "4. ETA & Compliance Data",
    //   body: "As an ETA-certified platform, we handle e-invoicing data in accordance with Egyptian Tax Authority regulations. This data is retained for the legally required period of 5 years and may be shared with the ETA as required by law.",
    // },
    {
      title: "5. Data Sharing",
      body: "We do not sell, trade, or rent your personal information to third parties. We may share data with trusted service providers who assist us in operating our platform, subject to confidentiality agreements.",
    },
    {
      title: "6. Your Rights",
      body: "You have the right to access, correct, or delete your personal data at any time. You may also request a copy of your data in a portable format. To exercise these rights, contact us at info@spadeplatform.com.",
    },
    {
      title: "7. Cookies",
      body: "We use cookies and similar tracking technologies to track activity on our platform and hold certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.",
    },
    {
      title: "8. Changes to This Policy",
      body: "We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the date at the top.",
    },
    {
      title: "9. Contact Us",
      body: "If you have questions about this Privacy Policy, please contact us at info@spadeplatform.com or write to us at our Cairo, Egypt office.",
    },
  ],
};

const TERMS_CONTENT = {
  hero: {
    label: "LEGAL",
    title: "Terms of Service",
    sub: `Last updated: January 1, 2026`,
  },
  sections: [
    {
      title: "1. Acceptance of Terms",
      body: "By accessing or using Spade ERP, you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using or accessing this service.",
    },
    {
      title: "2. Description of Service",
      body: "Spade ERP provides a cloud-based enterprise resource planning platform including accounting, inventory management, CRM, HR, sales, purchasing, and reporting modules. Features available depend on your subscription plan.",
    },
    {
      title: "3. Subscription & Payment",
      body: "Spade ERP is available on an annual subscription basis. Payment is due in advance for the subscription period. We currently accept bank transfers and InstaPay. Subscriptions auto-renew unless cancelled 30 days before renewal.",
    },
    {
      title: "4. Free Trial",
      body: "New accounts receive a 14-day free trial with full access to all features of the selected plan. No credit card is required during the trial. At the end of the trial, you will be moved to the Free plan unless you subscribe.",
    },
    {
      title: "5. Acceptable Use",
      body: "You agree not to use Spade ERP for any unlawful purpose, to violate any laws, to infringe intellectual property rights, to transmit harmful content, or to interfere with the platform's operation. We reserve the right to terminate accounts that violate these terms.",
    },
    {
      title: "6. Data Ownership",
      body: "You retain full ownership of all data you input into Spade ERP. We do not claim ownership over your business data. You may export your data at any time. Upon account termination, your data will be retained for 30 days before deletion.",
    },
    {
      title: "7. Uptime & SLA",
      body: "We target 99.9% platform availability. Planned maintenance will be communicated 48 hours in advance. In the event of extended downtime, subscription credits may be applied at our discretion.",
    },
    {
      title: "8. Limitation of Liability",
      body: "Spade ERP shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of the service. Our total liability shall not exceed the amount paid by you in the 12 months preceding the claim.",
    },
    {
      title: "9. Termination",
      body: "Either party may terminate the subscription at any time. Upon termination, your access to the platform will cease at the end of the current subscription period. We do not provide refunds for unused subscription time.",
    },
    {
      title: "10. Governing Law",
      body: "These Terms shall be governed by and construed in accordance with the laws of Egypt. Any disputes arising under these terms shall be subject to the exclusive jurisdiction of the courts of Cairo, Egypt.",
    },
  ],
};

const CONTENT_MAP = {
  about: ABOUT_CONTENT,
  privacy: PRIVACY_CONTENT,
  terms: TERMS_CONTENT,
};

// ── About layout ──────────────────────────────────────────────────────────────
const AboutLayout = ({ content }) => (
  <>
    {/* Stats bar */}
    <div style={{ background: T.dark, padding: "32px 24px" }}>
      <div
        style={{
          maxWidth: 800,
          margin: "0 auto",
          display: "flex",
          justifyContent: "space-around",
          flexWrap: "wrap",
          gap: 20,
        }}
      >
        {content.stats.map((s) => (
          <div key={s.label} style={{ textAlign: "center" }}>
            <div
              style={{
                fontSize: "clamp(28px,3vw,40px)",
                fontWeight: 900,
                color: T.white,
                letterSpacing: -1,
              }}
            >
              {s.val}
            </div>
            <div
              style={{
                fontSize: 13,
                color: T.chipText,
                opacity: 0.7,
                marginTop: 4,
              }}
            >
              {s.label}
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Sections */}
    <div style={{ maxWidth: 760, margin: "0 auto", padding: "64px 24px" }}>
      {content.sections.map((s) => (
        <div key={s.title} style={{ marginBottom: 48 }}>
          <h2
            style={{
              fontSize: 22,
              fontWeight: 800,
              color: T.dark,
              margin: "0 0 14px",
              letterSpacing: -0.5,
            }}
          >
            {s.title}
          </h2>
          {s.body.split("\n\n").map((para, i) => (
            <p
              key={i}
              style={{
                fontSize: 15.5,
                color: T.muted,
                lineHeight: 1.85,
                margin: "0 0 16px",
              }}
            >
              {para}
            </p>
          ))}
          <div style={{ height: 1, background: T.border, marginTop: 32 }} />
        </div>
      ))}

      {/* Values grid */}
      <div>
        <h2
          style={{
            fontSize: 22,
            fontWeight: 800,
            color: T.dark,
            margin: "0 0 24px",
            letterSpacing: -0.5,
          }}
        >
          Our Values
        </h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))",
            gap: 16,
          }}
        >
          {content.values.map((v) => (
            <div
              key={v.title}
              style={{
                background: T.white,
                borderRadius: 12,
                padding: "20px 18px",
                border: `1px solid ${T.border}`,
              }}
            >
              <div style={{ fontSize: 26, marginBottom: 10 }}>{v.icon}</div>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: T.dark,
                  marginBottom: 6,
                }}
              >
                {v.title}
              </div>
              <div style={{ fontSize: 13, color: T.muted, lineHeight: 1.65 }}>
                {v.desc}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </>
);

// ── Legal layout (Privacy / Terms) ────────────────────────────────────────────
const LegalLayout = ({ content }) => (
  <div style={{ maxWidth: 760, margin: "0 auto", padding: "64px 24px" }}>
    <div
      style={{
        background: "#fef3c7",
        border: "1px solid #fde68a",
        borderRadius: 10,
        padding: "12px 18px",
        marginBottom: 40,
        fontSize: 13,
        color: "#92400e",
      }}
    >
      📋 This document is for informational purposes. For legal questions,
      contact info@spadeplatform.com.
    </div>
    {content.sections.map((s, i) => (
      <div key={s.title} style={{ marginBottom: 36 }}>
        <h2
          style={{
            fontSize: 18,
            fontWeight: 700,
            color: T.dark,
            margin: "0 0 10px",
          }}
        >
          {s.title}
        </h2>
        <p
          style={{ fontSize: 15, color: T.muted, lineHeight: 1.85, margin: 0 }}
        >
          {s.body}
        </p>
        {i < content.sections.length - 1 && (
          <div style={{ height: 1, background: T.border, marginTop: 28 }} />
        )}
      </div>
    ))}
  </div>
);

// ── Main TextDataPage ─────────────────────────────────────────────────────────
// currentPageData.sub_type = 'about' | 'privacy' | 'terms'
// Falls back to 'about' if not set
const TextDataPage = ({ currentPageData }) => {
  const subType =
    currentPageData?.sub_type || currentPageData?.subType || "about";
  const content = CONTENT_MAP[subType] || ABOUT_CONTENT;

  return (
    <div
      style={{
        background: T.surface,
        minHeight: "100vh",
        fontFamily:
          "'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
      }}
    >
      {/* Hero */}
      <div
        style={{
          background: `linear-gradient(135deg, ${T.dark} 0%, ${T.darkDeep} 100%)`,
          padding: "72px 24px 80px",
          textAlign: "center",
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 3,
            color: T.chipText,
            marginBottom: 16,
          }}
        >
          {content.hero.label}
        </div>
        <h1
          style={{
            fontSize: "clamp(28px,4vw,48px)",
            fontWeight: 900,
            color: T.white,
            margin: "0 0 16px",
            letterSpacing: -1,
          }}
        >
          {content.hero.title}
        </h1>
        <p
          style={{
            fontSize: 16,
            color: T.chipText,
            opacity: 0.7,
            maxWidth: 520,
            margin: "0 auto",
            lineHeight: 1.7,
          }}
        >
          {content.hero.sub}
        </p>
      </div>

      {subType === "about" ? (
        <AboutLayout content={content} />
      ) : (
        <LegalLayout content={content} />
      )}
    </div>
  );
};

export default TextDataPage;
